if (!customElements.get('cart-quantity-breaks')) {
  class CartQuantityBreaks extends HTMLElement {
    static storageKey = 'cart-quantity-breaks';
    static requests = new Map();

    connectedCallback() {
      this.onBadgeClick = this.onBadgeClick.bind(this);
      this.addEventListener('click', this.onBadgeClick);
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onBadgeClick);
    }

    async render() {
      const { productUrl, quantity } = this.dataset;
      if (!productUrl) return;

      const tiers = await CartQuantityBreaks.getTiers(productUrl);
      if (!this.isConnected) return;

      const currentQuantity = Number(quantity);
      const badges = tiers
        .filter((tier) => tier.quantity > currentQuantity)
        .map((tier) => this.createBadge(tier));
      this.replaceChildren(...badges);
    }

    createBadge({ quantity, percent }) {
      const badge = document.createElement('button');
      badge.type = 'button';
      badge.className = 'cart-quantity-breaks__badge';
      badge.dataset.quantity = quantity;
      badge.textContent = this.dataset.label.replace('[quantity]', quantity).replace('[percent]', percent);
      return badge;
    }

    onBadgeClick(event) {
      const badge = event.target.closest('.cart-quantity-breaks__badge');
      if (!badge) return;

      const input = document.getElementById(this.dataset.inputId);
      if (!input) return;

      input.value = badge.dataset.quantity;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    static getTiers(productUrl) {
      const cached = CartQuantityBreaks.readCache()[productUrl];
      if (cached) return Promise.resolve(cached);

      if (!CartQuantityBreaks.requests.has(productUrl)) {
        const request = CartQuantityBreaks.fetchTiers(productUrl)
          .then((tiers) => {
            CartQuantityBreaks.writeCache(productUrl, tiers);
            return tiers;
          })
          .catch(() => [])
          .finally(() => CartQuantityBreaks.requests.delete(productUrl));
        CartQuantityBreaks.requests.set(productUrl, request);
      }

      return CartQuantityBreaks.requests.get(productUrl);
    }

    static async fetchTiers(productUrl) {
      const response = await fetch(productUrl);
      if (!response.ok) return [];

      const html = await response.text();
      const page = new DOMParser().parseFromString(html, 'text/html');
      const boxes = page.querySelectorAll('.qty-breaks-block .quantity-box[data-value]');

      return Array.from(boxes)
        .map((box) => {
          const quantity = Number.parseInt(box.dataset.value, 10);
          const percent = box.querySelector('.save')?.textContent.match(/(\d+)%/)?.[1];
          return quantity && percent ? { quantity, percent } : null;
        })
        .filter(Boolean);
    }

    static readCache() {
      try {
        return JSON.parse(sessionStorage.getItem(CartQuantityBreaks.storageKey)) ?? {};
      } catch {
        return {};
      }
    }

    static writeCache(productUrl, tiers) {
      try {
        const cache = CartQuantityBreaks.readCache();
        cache[productUrl] = tiers;
        sessionStorage.setItem(CartQuantityBreaks.storageKey, JSON.stringify(cache));
      } catch {
        return;
      }
    }
  }

  customElements.define('cart-quantity-breaks', CartQuantityBreaks);
}

if (!customElements.get('cart-upsell-slider')) {
  class CartUpsellSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.cart-upsell__track');
      this.arrows = this.querySelectorAll('.cart-upsell__arrow');
      this.onArrowClick = this.onArrowClick.bind(this);
      this.arrows.forEach((arrow) => arrow.addEventListener('click', this.onArrowClick));
    }

    disconnectedCallback() {
      this.arrows?.forEach((arrow) => arrow.removeEventListener('click', this.onArrowClick));
    }

    onArrowClick(event) {
      const slideCount = this.track?.children.length ?? 0;
      if (slideCount < 2) return;

      const direction = Number(event.currentTarget.dataset.direction);
      const slideWidth = this.track.clientWidth;
      const currentIndex = Math.round(this.track.scrollLeft / slideWidth);
      const nextIndex = (currentIndex + direction + slideCount) % slideCount;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.track.scrollTo({ left: nextIndex * slideWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }

  customElements.define('cart-upsell-slider', CartUpsellSlider);
}

if (!customElements.get('cart-protection-toggle')) {
  class CartProtectionToggle extends HTMLElement {
    static storageKey = 'cart-protection-wanted';
    static lastKnownPrice = '';

    connectedCallback() {
      this.switch = this.querySelector('.cart-protection__switch');
      this.priceElement = this.querySelector('[data-protection-price]');
      this.widget = this.closest('.cart-drawer__footer, .cart__blocks')?.querySelector('.nvd-mini');
      if (!this.switch || !this.widget || this.isDrawerOnCartPage()) return;

      this.onSwitchClick = this.onSwitchClick.bind(this);
      this.switch.addEventListener('click', this.onSwitchClick);

      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.widget, { childList: true, subtree: true });

      if (CartProtectionToggle.lastKnownPrice) this.show(CartProtectionToggle.lastKnownPrice);
      this.sync();
    }

    disconnectedCallback() {
      this.switch?.removeEventListener('click', this.onSwitchClick);
      this.observer?.disconnect();
    }

    isDrawerOnCartPage() {
      return Boolean(this.closest('cart-drawer') && document.getElementById('main-cart-footer'));
    }

    get checkbox() {
      return this.widget.querySelector('#shippingProtectionCheckBox');
    }

    get wanted() {
      try {
        return sessionStorage.getItem(CartProtectionToggle.storageKey) === 'true';
      } catch {
        return false;
      }
    }

    set wanted(value) {
      try {
        sessionStorage.setItem(CartProtectionToggle.storageKey, String(value));
      } catch {
        return;
      }
    }

    sync() {
      const { checkbox } = this;
      if (!checkbox) return;

      const price = this.widget.querySelector('#shipping-protection-price')?.textContent.trim() ?? '';
      if (price) CartProtectionToggle.lastKnownPrice = price;
      this.show(price || CartProtectionToggle.lastKnownPrice);
      this.applyWantedState(checkbox);
    }

    show(price) {
      this.priceElement.textContent = price;
      this.switch.setAttribute('aria-checked', String(this.wanted));
      this.hidden = false;
    }

    onSwitchClick() {
      this.wanted = !this.wanted;
      this.switch.setAttribute('aria-checked', String(this.wanted));

      const { checkbox } = this;
      if (checkbox) this.applyWantedState(checkbox);
    }

    applyWantedState(checkbox) {
      if (checkbox.checked === this.wanted) return;

      checkbox.click();
      setTimeout(() => {
        const popup = document.getElementById('nvd-popup');
        const confirmButton = document.getElementById('nvd-popup-confirm');
        if (popup && popup.style.display !== 'none') confirmButton?.click();
      }, 150);
    }
  }

  customElements.define('cart-protection-toggle', CartProtectionToggle);
}
